import React, { useMemo } from "react";
import { ScreenShell, WheelPicker } from "../components";
import { T } from "../theme";

type Props = {
  step: number;
  total: number;
  value: string; // YYYY-MM-DD
  onChange: (v: string) => void;
  onNext: () => void;
  onBack: () => void;
  isMobile: boolean;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function daysInMonth(year: number, monthIdx: number): number {
  return new Date(year, monthIdx + 1, 0).getDate();
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

export function DOBScreen({ step, total, value, onChange, onNext, onBack, isMobile }: Props): React.ReactElement {
  const now = new Date();
  const minYear = 1925;
  const maxYear = now.getFullYear() - 13; // 13+ only

  const parts = useMemo(() => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
    if (!m) {
      // default: Jan 1, 1996
      return { y: 1996, mo: 0, d: 1 };
    }
    return { y: parseInt(m[1], 10), mo: parseInt(m[2], 10) - 1, d: parseInt(m[3], 10) };
  }, [value]);

  const monthValues = MONTHS;
  const yearValues = useMemo(() => {
    const out: string[] = [];
    for (let y = maxYear; y >= minYear; y -= 1) out.push(String(y));
    return out;
  }, [maxYear]);
  const daysCount = daysInMonth(parts.y, parts.mo);
  const dayValues = useMemo(() => Array.from({ length: daysCount }, (_, i) => String(i + 1)), [daysCount]);

  const yearIndex = Math.max(0, yearValues.indexOf(String(parts.y)));
  const monthIndex = parts.mo;
  const dayIndex = Math.min(daysCount - 1, parts.d - 1);

  function emit(y: number, m: number, d: number): void {
    const safeDay = Math.min(d, daysInMonth(y, m));
    onChange(`${y}-${pad(m + 1)}-${pad(safeDay)}`);
  }

  const age = (() => {
    const today = new Date();
    let a = today.getFullYear() - parts.y;
    const md = today.getMonth() - parts.mo;
    if (md < 0 || (md === 0 && today.getDate() < parts.d)) a -= 1;
    return a;
  })();

  return (
    <ScreenShell
      step={step}
      total={total}
      title="When were you born?"
      subtitle="Age refines your BMR estimate."
      onBack={onBack}
      isMobile={isMobile}
      primaryLabel="Continue"
      primaryDisabled={age < 13 || age > 120}
      onPrimary={onNext}
    >
      <div style={{ marginTop: 8 }}>
        <WheelPicker
          columns={[
            {
              values: monthValues,
              selectedIndex: monthIndex,
              onChange: (i) => emit(parts.y, i, parts.d),
            },
            {
              values: dayValues,
              selectedIndex: dayIndex,
              onChange: (i) => emit(parts.y, parts.mo, i + 1),
            },
            {
              values: yearValues,
              selectedIndex: yearIndex,
              onChange: (i) => emit(parseInt(yearValues[i], 10), parts.mo, parts.d),
            },
          ]}
        />
        <div style={{ marginTop: 18, textAlign: "center", color: T.fg2, fontSize: "0.86rem", fontWeight: 600 }}>
          {age >= 13 && age <= 120 ? `${age} years old` : "Enter a valid date"}
        </div>
      </div>
    </ScreenShell>
  );
}
